/**
 * Moving a run of lessons, without moving them an hour.
 *
 * A term shift is "same day, same time, N weeks later", and that is a statement
 * about the *clock on the wall*, not about elapsed time. Adding 7 × 86 400 000
 * milliseconds is the obvious implementation and it is wrong twice a year:
 * every lesson shifted across the last Sunday in October arrives an hour early
 * and every one shifted across the last Sunday in March an hour late. Nobody
 * would find that in testing in August. A family would find it in November.
 *
 * So the arithmetic is done on London wall-clock components and converted back
 * to an instant afterwards.
 *
 * **Every unit follows that same rule, including hours and minutes.** "An hour
 * later" is a reading on a clock, not sixty minutes of elapsed time, and the
 * two differ on exactly the night the clocks change. Treating the small units
 * as elapsed time and the large ones as wall-clock would be the worst of both:
 * two rules, one boundary between them, and a disagreement nobody would think
 * to look for. So a shift is one naive wall-clock displacement whatever unit
 * somebody chose it in, and the conversion back to a real instant happens once,
 * at the end, in `instantOf`.
 */

const LONDON = "Europe/London";

const parts = new Intl.DateTimeFormat("en-GB", {
  timeZone: LONDON,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** What a clock in London reads at a given instant. */
function londonWallClock(at: Date): WallClock {
  const found: Record<string, string> = {};
  for (const part of parts.formatToParts(at)) {
    if (part.type !== "literal") found[part.type] = part.value;
  }
  return {
    year: Number(found["year"]),
    // `hour12: false` yields 24 for midnight in some engines, which is the
    // same instant as hour 0 and would otherwise roll a day forward below.
    month: Number(found["month"]),
    day: Number(found["day"]),
    hour: Number(found["hour"]) % 24,
    minute: Number(found["minute"]),
    second: Number(found["second"]),
  };
}

/** How far London is from UTC at a given instant, in milliseconds. */
function londonOffset(at: Date): number {
  const clock = londonWallClock(at);
  const asUtc = Date.UTC(
    clock.year,
    clock.month - 1,
    clock.day,
    clock.hour,
    clock.minute,
    clock.second,
  );
  return asUtc - at.getTime();
}

/**
 * A London wall-clock reading, back to the instant it names.
 *
 * Solved rather than looked up: guess that the offset is what it was at the
 * naive instant, correct once, and the answer is right either side of a
 * transition. The second pass is what handles a shift that crosses one.
 *
 * The two hours a year that have no single answer — the clock going forward
 * skips 01:00–02:00, going back repeats it — resolve to the earlier instant.
 * No lesson is taught at half past one in the morning, so this is written down
 * rather than guarded against.
 */
function instantOf(clock: WallClock): Date {
  const naive = Date.UTC(
    clock.year,
    clock.month - 1,
    clock.day,
    clock.hour,
    clock.minute,
    clock.second,
  );
  const firstGuess = new Date(naive - londonOffset(new Date(naive)));
  return new Date(naive - londonOffset(firstGuess));
}

/** The units a staff member can express a move in. */
export type ShiftUnit = "weeks" | "days" | "hours" | "minutes";

/**
 * What one unit spans on a wall clock.
 *
 * Exact, because this is naive arithmetic: a "day" here moves the date by one
 * and leaves the time of day alone, which is what a day means to somebody
 * moving a lesson. Whether that day happens to contain 23 or 25 real hours is
 * settled afterwards by `instantOf`, and is not this table's business.
 */
const UNIT_MS: Record<ShiftUnit, number> = {
  weeks: 7 * 86_400_000,
  days: 86_400_000,
  hours: 3_600_000,
  minutes: 60_000,
};

/**
 * A London wall-clock reading as a naive instant — the number to do arithmetic
 * on, and never a real point in time.
 *
 * Naive UTC has no daylight saving, which is exactly why the sums happen here:
 * every day is 24 hours long, so adding one moves the date and nothing else.
 */
function naiveOf(iso: string): number {
  const clock = londonWallClock(new Date(iso));
  return Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute, clock.second);
}

/** A naive instant back to the real one it names in London. */
function fromNaive(naive: number): string {
  const at = new Date(naive);
  return instantOf({
    year: at.getUTCFullYear(),
    month: at.getUTCMonth() + 1,
    day: at.getUTCDate(),
    hour: at.getUTCHours(),
    minute: at.getUTCMinutes(),
    second: at.getUTCSeconds(),
  }).toISOString();
}

/**
 * Move a lesson by a wall-clock displacement.
 *
 * The one primitive underneath every control on the screen. Adding, taking
 * away and moving to a chosen time are all the same operation with a different
 * displacement, so there is one place where a lesson's new time is worked out
 * and one place for it to be wrong.
 */
export function shiftByNaive(iso: string, naiveMs: number): string {
  return fromNaive(naiveOf(iso) + naiveMs);
}

/** How far a displacement is, in the unit somebody chose it in. */
export function displacementOf(amount: number, unit: ShiftUnit): number {
  return amount * UNIT_MS[unit];
}

/**
 * The same time of day, `amount` units later or earlier.
 *
 * Negative moves a run backwards, which is the less common half of the same
 * job — a term starting a week sooner than planned.
 */
export function shiftBy(iso: string, amount: number, unit: ShiftUnit): string {
  return shiftByNaive(iso, displacementOf(amount, unit));
}

/**
 * The displacement that lands `iso` on a chosen wall-clock time.
 *
 * This is what "changing to" means for a *run* of lessons: the time is chosen
 * for the first one, and the rest keep their spacing rather than collapsing
 * onto the same slot. Twelve lessons moved to Tuesday half past four are one
 * lesson moved and eleven following it, which is the only reading that leaves
 * a term looking like a term.
 *
 * `target` is what a `datetime-local` input gives — a wall-clock reading with
 * no zone, which is precisely what this arithmetic wants. Null for anything
 * unparseable, so a half-typed box is "not yet" rather than a move to 1970.
 */
export function displacementTo(iso: string, target: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(target.trim());
  if (!match) return null;

  const [, year, month, day, hour, minute, second] = match;
  const wanted = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? "0"),
  );
  if (Number.isNaN(wanted)) return null;

  return wanted - naiveOf(iso);
}

/**
 * A lesson's time as a `datetime-local` value, for seeding the target box.
 *
 * Seeded from the first selected lesson rather than left empty or set to now:
 * "change to" almost always means changing part of a time somebody is already
 * looking at — the same slot a week on, or the same day an hour later — and
 * typing a full date to alter one field of it is how a wrong month gets typed.
 */
export function asLocalInput(iso: string): string {
  const clock = londonWallClock(new Date(iso));
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${clock.year}-${pad(clock.month)}-${pad(clock.day)}T${pad(clock.hour)}:${pad(clock.minute)}`;
}

/** Whether a shift crosses a change of the clocks, so the page can say so. */
export function crossesClockChange(iso: string, naiveMs: number): boolean {
  if (naiveMs === 0) return false;
  const from = londonOffset(new Date(iso));
  const to = londonOffset(new Date(shiftByNaive(iso, naiveMs)));
  return from !== to;
}
